import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  RiFileTextLine, 
  RiDownloadLine, 
  RiCalendarLine,
  RiAlarmLine,
  RiPresentationLine,
  RiListCheck,
  RiArrowRightLine,
  RiExternalLinkLine,
  RiMailLine,
  RiPhoneLine,
  RiMapPinLine,
  RiCheckLine,
  RiStarLine,
  RiTimerLine,
  RiUserVoiceLine,
  RiBarChartLine,
} from 'react-icons/ri';

/* ─── FONT IMPORT & GLOBAL STYLES ───────────────────────────────────────────── */
const FontImport = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { scroll-behavior: smooth; }
    ::-webkit-scrollbar { width: 8px; }
    ::-webkit-scrollbar-track { background: #E2E8F0; }
    ::-webkit-scrollbar-thumb { background: #1A56DB; border-radius: 4px; }
  `}</style>
);

/* ─── THEME COLORS (Blue & White) ───────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F8FAFC",
  softBlue: "#EFF6FF",
  primary: "#1A56DB",
  primaryDark: "#1240A8",
  primaryLight: "#3B82F6",
  navy: "#0F2557",
  navyDeep: "#091A3E",
  accent: "#60A5FA",
  accentGold: "#F59E0B",
  text: "#1E293B",
  textMid: "#475569",
  textMuted: "#64748B",
  border: "#E2E8F0",
  borderLight: "#F1F5F9",
  shadow: "0 4px 20px rgba(0,0,0,0.05)",
  shadowMd: "0 8px 30px rgba(0,0,0,0.08)",
  shadowLg: "0 20px 40px rgba(0,0,0,0.1)",
  gradPrimary: "linear-gradient(135deg, #1A56DB 0%, #3B82F6 100%)",
  gradNavy: "linear-gradient(135deg, #0F2557 0%, #1A56DB 100%)",
  gradSoft: "linear-gradient(135deg, #EFF6FF 0%, #F8FAFC 100%)",
  gradGold: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)",
};

const presentationTypes = [
  { name: 'Plenary Presentation', duration: '40 minutes including Q&A', icon: '🎤', color: T.primary },
  { name: 'Keynote Presentation', duration: '30 minutes including Q&A', icon: '🔑', color: T.accent },
  { name: 'Poster Presentation', duration: 'in person at the Congress', icon: '📊', color: '#8B5CF6' },
  { name: 'Invited Presentation', duration: '25 minutes including Q&A', icon: '⭐', color: T.accentGold },
  { name: 'Oral Presentation', duration: '20 minutes including Q&A', icon: '🎙️', color: '#10B981' },
];

const keyDates = [
  { event: 'Super Earlybird Registration', date: 'August 28, 2025', icon: '🌟', color: T.primary },
  { event: 'Earlybird Registration', date: 'November 28, 2025', icon: '🐦', color: T.accent },
  { event: 'Standard Registration', date: 'May 10, 2026', icon: '📝', color: '#8B5CF6' },
  { event: 'Abstract Submission', date: 'April 15, 2026', icon: '📄', color: T.accentGold },
];

const guidelines = [
  'Abstracts are to be submitted through online submission system only. Faxes or emails will not be accepted.',
  'Abstracts must be in English, in Word Doc or PDF format, Should not exceed 500 words.',
  'The presenting author should obtain necessary regulatory permissions and take responsibility for the accuracy of the results.',
  'An e-mail confirming the receipt of abstract, including an abstract number, will be sent to the submitting author.',
  'Modification of submitted abstracts will be permitted only if the author informs conference secretary through email prior 60 days of the summit.',
  'Abstracts will be reviewed by committee and selected for oral/poster presentation on the basis of merit.',
  'The acceptance and mode of presentation will be intimated to authors via e-mail within 7 working days from the date of submission.',
  'The meeting rooms are equipped with data projectors thus only MS PowerPoint will be accepted.',
  'Conflict of Interest: If the research is funded (partially or fully) by a proprietary organization, a statement to that effect must be included.',
  'Please note that no more than two submissions will be allowed per presenter due to possible program limitations.',
  'Where possible abstracts will be published as received and the organizers reserve the right to make changes for editing purposes.',
  'The author has to make the mode of presentation clear at the time of submission of abstracts.',
];

export default function CallForAbstracts() {
  const handleDownloadTemplate = () => {
    const templateContent = `CALL FOR ABSTRACTS TEMPLATE - IMMUNOLOGY SUMMIT 2026

================================================================================

ABSTRACT INFORMATION
═══════════════════════════════════════════════════════════════════════════════

Title: 
[Provide a clear and concise title for your abstract]

Authors: 
[List all authors with full names]

Affiliations: 
[Institutional affiliations for each author]

Abstract (max 500 words): 
[Background/Introduction, Methods, Results, Conclusion]


PRESENTATION PREFERENCE
═══════════════════════════════════════════════════════════════════════════════

Preferred Presentation Type (choose one):
□ Plenary Presentation (40 min)
□ Keynote Presentation (30 min)  
□ Poster Presentation
□ Invited Presentation (25 min)
□ Oral Presentation (20 min)


TOPIC CATEGORY (choose primary topic)
═══════════════════════════════════════════════════════════════════════════════

[Select from: Adaptive Immunity, Innate Immunity, Autoimmunity, 
 Cancer Immunology, Vaccine Development, Clinical Immunology, 
 Immunodeficiencies, Infection & Immunity, Mucosal Immunology, 
 Neuroimmunology, Reproductive Immunology, Transplantation Immunology]


FUNDING INFORMATION
═══════════════════════════════════════════════════════════════════════════════

Funding Source (if any): 
Conflict of Interest Declaration: 


ADDITIONAL NOTES
═══════════════════════════════════════════════════════════════════════════════

[Any additional information relevant to your submission]

================================================================================
Please ensure your abstract does not exceed 500 words.
Submit through the online portal at: https://immunologysummit.synergiasummits.com/submit
================================================================================
`;
    const blob = new Blob([templateContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'call_for_abstracts_template.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSubmitAbstract = () => {
    window.open('https://immunologysummit.synergiasummits.com/submit', '_blank');
  };

  return (
    <div style={{ minHeight: '100vh', background: T.offWhite, fontFamily: "'Inter', sans-serif" }}>
      <FontImport />

      {/* Header Navigation */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: T.white,
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${T.borderLight}`,
        padding: '16px 48px',
        boxShadow: T.shadow,
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'baseline', gap: 2 }}>
            <span style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 28,
              fontWeight: 900,
              background: T.gradPrimary,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>Meeting</span>
            <span style={{ fontSize: 28, fontWeight: 900, color: T.text }}>21</span>
          </Link>
          
          <div style={{ display: 'flex', gap: 32, alignItems: 'center', flexWrap: 'wrap' }}>
            <Link to="/events" style={{ color: T.textMuted, textDecoration: 'none', fontSize: 14, fontWeight: 500, transition: 'color 0.2s' }} onMouseEnter={e => e.currentTarget.style.color = T.primary} onMouseLeave={e => e.currentTarget.style.color = T.textMuted}>Events</Link>
            <Link to="/about" style={{ color: T.textMuted, textDecoration: 'none', fontSize: 14, fontWeight: 500, transition: 'color 0.2s' }} onMouseEnter={e => e.currentTarget.style.color = T.primary} onMouseLeave={e => e.currentTarget.style.color = T.textMuted}>About</Link>
            <Link to="/venue" style={{ color: T.textMuted, textDecoration: 'none', fontSize: 14, fontWeight: 500, transition: 'color 0.2s' }} onMouseEnter={e => e.currentTarget.style.color = T.primary} onMouseLeave={e => e.currentTarget.style.color = T.textMuted}>Venue</Link>
            <Link to="/awards" style={{ color: T.textMuted, textDecoration: 'none', fontSize: 14, fontWeight: 500, transition: 'color 0.2s' }} onMouseEnter={e => e.currentTarget.style.color = T.primary} onMouseLeave={e => e.currentTarget.style.color = T.textMuted}>Awards</Link>
            <Link to="/call-for-abstracts" style={{ color: T.primary, textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>Call for Abstracts</Link>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section style={{
        textAlign: 'center',
        padding: '80px 24px 60px',
        position: 'relative',
        overflow: 'hidden',
        background: T.gradNavy,
      }}>
        <div style={{
          position: 'absolute',
          width: '60vw',
          height: '60vw',
          background: `radial-gradient(circle, rgba(59,130,246,0.15), transparent 70%)`,
          top: '-30%',
          right: '-30%',
          borderRadius: '50%',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute',
          width: '50vw',
          height: '50vw',
          background: `radial-gradient(circle, rgba(96,165,250,0.08), transparent 70%)`,
          bottom: '-20%',
          left: '-20%',
          borderRadius: '50%',
          pointerEvents: 'none',
        }} />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 12,
            background: 'rgba(255,255,255,0.12)',
            border: `1px solid rgba(255,255,255,0.2)`,
            borderRadius: 60,
            padding: '8px 24px',
            marginBottom: 28,
          }}
        >
          <RiFileTextLine size={16} style={{ color: T.accent }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: T.white, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Call for Submissions
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(48px, 7vw, 80px)',
            fontWeight: 900,
            color: T.white,
            marginBottom: 20,
          }}
        >
          Call for{' '}
          <span style={{
            background: T.gradPrimary,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>Abstracts
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          style={{
            color: T.accent,
            fontSize: 18,
            maxWidth: 750,
            margin: '0 auto',
            lineHeight: 1.6,
          }}
        >
          Share your groundbreaking research with the global immunology community. Submit your abstract for consideration at the premier immunology conference of 2026.
        </motion.p>
      </section>

      {/* Main Content */}
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '20px 24px 80px' }}>
        
        {/* Introduction Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: T.white,
            borderRadius: 28,
            border: `1px solid ${T.borderLight}`,
            padding: '40px',
            marginBottom: 40,
            boxShadow: T.shadowLg,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: T.softBlue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <RiUserVoiceLine size={24} style={{ color: T.primary }} />
            </div>
            <h2 style={{ fontSize: 24, fontWeight: 800, color: T.text }}>Contribute to the Global Discussion</h2>
          </div>
          
          <p style={{ fontSize: 16, color: T.textMid, lineHeight: 1.7, marginBottom: 24 }}>
            You are invited to contribute to the global discussion and submit an abstract for consideration 
            for inclusion in the Scientific Program. Please read the information on this page carefully and 
            when you are ready to submit your abstract please click the button below which will open the 
            Call for Abstracts portal in a new window.
          </p>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <motion.button
              onClick={handleSubmitAbstract}
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                background: T.gradPrimary,
                color: T.white,
                padding: '14px 32px',
                borderRadius: 50,
                fontSize: 15,
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                boxShadow: T.shadow,
              }}
            >
              Submit Abstract Now <RiExternalLinkLine size={16} />
            </motion.button>

            <motion.button
              onClick={handleDownloadTemplate}
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                background: T.white,
                color: T.primary,
                padding: '14px 32px',
                borderRadius: 50,
                fontSize: 15,
                fontWeight: 600,
                border: `1px solid ${T.border}`,
                cursor: 'pointer',
                boxShadow: T.shadow,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = T.softBlue; e.currentTarget.style.borderColor = T.primary; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = T.white; e.currentTarget.style.borderColor = T.border; }}
            >
              <RiDownloadLine size={16} /> Download Template
            </motion.button>
          </div>
        </motion.div>

        {/* Two Column Layout */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: 32 }}>
          {/* Left Column */}
          <div>
            {/* Key Dates */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              style={{
                background: T.white,
                borderRadius: 24,
                border: `1px solid ${T.borderLight}`,
                padding: '32px',
                marginBottom: 32,
                boxShadow: T.shadowMd,
              }}
              whileHover={{ y: -4, boxShadow: T.shadowLg }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                <div style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: T.softBlue,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <RiCalendarLine size={22} style={{ color: T.primary }} />
                </div>
                <h3 style={{ fontSize: 22, fontWeight: 800, color: T.text }}>Key Dates</h3>
              </div>
              {keyDates.map((item, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '14px 0',
                  borderBottom: idx < keyDates.length - 1 ? `1px solid ${T.borderLight}` : 'none',
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 20 }}>{item.icon}</span>
                    <span style={{ fontWeight: 500, color: T.textMid }}>{item.event}</span>
                  </span>
                  <span style={{ fontWeight: 700, color: item.color }}>{item.date}</span>
                </div>
              ))}
            </motion.div>

            {/* Presentation Types */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              style={{
                background: T.white,
                borderRadius: 24,
                border: `1px solid ${T.borderLight}`,
                padding: '32px',
                boxShadow: T.shadowMd,
              }}
              whileHover={{ y: -4, boxShadow: T.shadowLg }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                <div style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: T.softBlue,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <RiPresentationLine size={22} style={{ color: T.primary }} />
                </div>
                <h3 style={{ fontSize: 22, fontWeight: 800, color: T.text }}>Presentation Types</h3>
              </div>
              {presentationTypes.map((type, idx) => (
                <div key={idx} style={{
                  padding: '14px 0',
                  borderBottom: idx < presentationTypes.length - 1 ? `1px solid ${T.borderLight}` : 'none',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                    <span style={{ fontSize: 22 }}>{type.icon}</span>
                    <span style={{ fontWeight: 700, color: T.text }}>{type.name}</span>
                  </div>
                  <p style={{ fontSize: 13, color: T.textMuted, marginLeft: 34 }}>
                    <RiTimerLine size={12} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
                    {type.duration}
                  </p>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right Column - Guidelines */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            style={{
              background: T.white,
              borderRadius: 24,
              border: `1px solid ${T.borderLight}`,
              padding: '32px',
              boxShadow: T.shadowMd,
              height: 'fit-content',
            }}
            whileHover={{ y: -4, boxShadow: T.shadowLg }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <div style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: T.softBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <RiListCheck size={22} style={{ color: T.primary }} />
              </div>
              <h3 style={{ fontSize: 22, fontWeight: 800, color: T.text }}>Submission Guidelines</h3>
            </div>
            <div style={{ maxHeight: 520, overflowY: 'auto', paddingRight: 12 }}>
              {guidelines.map((guideline, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  gap: 12,
                  marginBottom: 16,
                  padding: '8px 0',
                }}>
                  <div style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: T.softBlue,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: 2,
                  }}>
                    <RiCheckLine size={12} style={{ color: T.primary }} />
                  </div>
                  <span style={{ fontSize: 14, color: T.textMid, lineHeight: 1.5 }}>{guideline}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* CTA Section */}
      <section style={{
        padding: '60px 48px',
        textAlign: 'center',
        background: T.gradPrimary,
        marginTop: 20,
      }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          style={{ maxWidth: 700, margin: '0 auto' }}
        >
          <RiStarLine size={40} style={{ color: T.white, marginBottom: 20, opacity: 0.9 }} />
          <h2 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(32px, 4vw, 42px)',
            fontWeight: 800,
            color: T.white,
            marginBottom: 16,
          }}>
            Ready to Share Your Research?
          </h2>
          <p style={{ fontSize: 16, color: `${T.white}cc`, marginBottom: 32, lineHeight: 1.6 }}>
            Submit your abstract today and join the world's leading immunology researchers at Meeting21.
          </p>
          <motion.button
            onClick={handleSubmitAbstract}
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              background: T.white,
              color: T.primary,
              padding: '14px 40px',
              borderRadius: 50,
              fontSize: 16,
              fontWeight: 700,
              border: 'none',
              cursor: 'pointer',
              boxShadow: T.shadow,
            }}
          >
            Submit Abstract Now <RiArrowRightLine size={16} />
          </motion.button>
          <p style={{ fontSize: 12, color: `${T.white}aa`, marginTop: 24 }}>
            Abstract submission deadline: April 15, 2026
          </p>
        </motion.div>
      </section>

   
    </div>
  );
}